// src/pages/AdminDashboard.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import EditDialog from "../components/EditDialog";

import {
  // ---- fetch helpers ------------------------------------------------------
  fetchAllOnboardingExisting,
  fetchAllOnboardingNew,
  fetchAllSubAccountRequests,
  fetchAllSubAccountConsents,

  // ---- create / approve helpers ------------------------------------------
  createPlatformUser,

  // ---- status helpers -----------------------------------------------------
  updateOnboardingExistingStatus,
  updateOnboardingNewStatus,
  updateSubAccountRequestStatus,
  updateSubAccountConsentStatus,

  // ---- misc helpers -------------------------------------------------------
  patchOnboardingNewPassword,

  // ---- 🆕 generic PATCH helpers for modal ---------------------------------
  patchOnboardingExisting,
  patchOnboardingNew,
} from "../services/api";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://forms.ttanalytics.net";
const INFO_BASE_URL = "https://info.ttanalytics.net";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function AdminDashboard() {
  // active tab selector
  const [activeTab, setActiveTab] = useState("existing");

  // data buckets
  const [existingData, setExistingData] = useState([]);
  const [newData, setNewData] = useState([]);
  const [requestData, setRequestData] = useState([]);
  const [consentData, setConsentData] = useState([]);

  // misc UI
  const [error, setError] = useState("");

  // expanded‑row state
  const [expandedExisting, setExpandedExisting] = useState(new Set());
  const [expandedNew, setExpandedNew] = useState(new Set());
  const [expandedRequest, setExpandedRequest] = useState(new Set());
  const [expandedConsent, setExpandedConsent] = useState(new Set());

  // manual user‑code entry for onboarding‑new
  const [newUserCodes, setNewUserCodes] = useState({});

  // 🆕 edit‑dialog state
  // { type: "existing" | "new", data: <row> } | null
  const [editing, setEditing] = useState(null);

  // bearer token
  const token = localStorage.getItem("adminToken");

  // -------------------------------------------------------------------------
  // Excel export helper
  // -------------------------------------------------------------------------
  const exportExcel = async (endpoint, sheetTitle) => {
    try {
      const response = await axios.get(`${BASE_URL}/api${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${sheetTitle}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Excel export error:", err);
      alert("Export failed. Please try again.");
    }
  };

  // -------------------------------------------------------------------------
  // PDF download helper (full filled form + terms + signature + ID declaration)
  // -------------------------------------------------------------------------
  const downloadPdf = async (endpoint, filename) => {
    try {
      const response = await axios.get(`${BASE_URL}/api${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("PDF download error:", err);
      alert("PDF download failed. Please try again.");
    }
  };

  // -------------------------------------------------------------------------
  // Fetch all data once on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      try {
        const [
          existingResult,
          newResult,
          requestResult,
          consentResult,
        ] = await Promise.all([
          fetchAllOnboardingExisting(token),
          fetchAllOnboardingNew(token),
          fetchAllSubAccountRequests(token),
          fetchAllSubAccountConsents(token),
        ]);

        setExistingData(existingResult);
        setNewData(newResult);
        setRequestData(requestResult);
        setConsentData(consentResult);

        // pre‑fill user‑code inputs
        const codes = {};
        newResult.forEach((r) => (codes[r.user_id] = r.user_code || ""));
        setNewUserCodes(codes);
      } catch (err) {
        console.error("Error fetching admin data:", err);
        setError(
          "Failed to load form data. Check server logs or endpoint availability."
        );
      }
    };

    fetchData();
  }, [token]);

  // -------------------------------------------------------------------------
  // 🆕  Save handler for the Edit modal
  // -------------------------------------------------------------------------
  // const handleEditSave = async (patch, original) => {
  //   const auth = localStorage.getItem("adminToken");

  //   try {
  //     if (editing.type === "existing") {
  //       await patchOnboardingExisting(original.user_id, patch, auth);
  //       setExistingData((prev) =>
  //         prev.map((r) =>
  //           r.user_id === original.user_id ? { ...r, ...patch } : r
  //         )
  //       );
  //     } else {
  //       await patchOnboardingNew(original.user_id, patch, auth);
  //       setNewData((prev) =>
  //         prev.map((r) =>
  //           r.user_id === original.user_id ? { ...r, ...patch } : r
  //         )
  //       );
  //     }
  //     alert("Record updated!");
  //   } catch (err) {
  //     console.error("EditSave error:", err);
  //     alert("Update failed. See console.");
  //   } finally {
  //     setEditing(null);
  //   }
  // };

// ---------------------------------------------------------------------------
// Save-handler for the Edit modal – sends a minimal, clean PATCH payload
// ---------------------------------------------------------------------------
const handleEditSave = async (draft, original) => {
  const token = localStorage.getItem("adminToken");

  /* ---------- 1. columns the backend update-schemas accept --------------- */
  const allowed = new Set([
    "first_name","last_name",
    "email1","email2",
    "date_of_birth",
    "address_line_1","address_line_2","address_city","postcode","county",
    "phone_number","occupation","working_hours",
    "payment_full_name","payment_sort_code","payment_account_number",
    "referral1","referral2",
    "high_demand_events","buying_more_tickets","transfer_service",
    "allow_tt_signups","all_tt_manage_passwords","allow_tt_create_emails",
    "live_rebate_updates","service_agreement","privacy_policy",
    "user_code","status_notes",
    ...(editing.type === "new" ? ["crm_password"] : []),
  ]);

  /* ---------- 2. build a diff: only changed keys, "" → null -------------- */
  const payload = {};
  for (const [k, v] of Object.entries(draft)) {
    if (!allowed.has(k)) continue;

    // normalise: turn blank strings into null
    const normalised =
      typeof v === "string" && v.trim() === "" ? null : v;

    // include the key only if the value actually changed
    if (normalised !== original[k]) {
      payload[k] = normalised;
    }
  }

  // nothing changed – just close the modal
  if (Object.keys(payload).length === 0) {
    alert("Nothing changed.");
    setEditing(null);
    return;
  }

  /* ---------- 3. call the correct PATCH endpoint ------------------------- */
  try {
    if (editing.type === "existing") {
      await patchOnboardingExisting(original.user_id, payload, token);
      setExistingData(prev =>
        prev.map(r =>
          r.user_id === original.user_id ? { ...r, ...payload } : r
        )
      );
    } else {
      await patchOnboardingNew(original.user_id, payload, token);
      setNewData(prev =>
        prev.map(r =>
          r.user_id === original.user_id ? { ...r, ...payload } : r
        )
      );
    }
    alert("Record updated!");
  } catch (err) {
    console.error("EditSave error:", err?.response?.data || err);
    alert("Update failed – see console for details.");
  } finally {
    setEditing(null);
  }
};


  // -------------------------------------------------------------------------
  // View helpers (Existing / New / Consent)
  // -------------------------------------------------------------------------
  const viewExistingDocument = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/onboarding/existing/${userId}/file`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.signed_url, "_blank");
    } catch (err) {
      console.error("Error fetching existing document:", err);
      alert("Could not load existing document. Please try again.");
    }
  };

  const viewExistingSignature = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/onboarding/existing/${userId}/signature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.signed_url, "_blank");
    } catch (err) {
      console.error("Error fetching existing signature:", err);
      alert("Could not load existing signature. Please try again.");
    }
  };

  const viewNewDocument = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/onboarding/new/${userId}/file`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.signed_url, "_blank");
    } catch (err) {
      console.error("Error fetching new document:", err);
      alert("Could not load new document. Please try again.");
    }
  };

  const viewNewSignature = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/onboarding/new/${userId}/signature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.signed_url, "_blank");
    } catch (err) {
      console.error("Error fetching new signature:", err);
      alert("Could not load new signature. Please try again.");
    }
  };

  const viewConsentDocument = async (consentId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}/file`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.signed_url, "_blank");
    } catch (err) {
      console.error("Error fetching consent document:", err);
      alert("Could not load consent document. Please try again.");
    }
  };

  const viewConsentSignature = async (consentId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}/signature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.signed_url, "_blank");
    } catch (err) {
      console.error("Error fetching consent signature:", err);
      alert("Could not load consent signature. Please try again.");
    }
  };

  // -------------------------------------------------------------------------
  // Delete / update helpers  –– EXISTING
  // -------------------------------------------------------------------------
  const deleteExistingDocument = async (userId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/onboarding/existing/${userId}/document`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Document deleted successfully");
      setExistingData((prev) =>
        prev.map((r) =>
          r.user_id === userId ? { ...r, id_document_s3_path: null } : r
        )
      );
    } catch (err) {
      console.error("Error deleting existing document:", err);
      alert("Failed to delete document. Please try again.");
    }
  };

  const updateExistingDocument = async (userId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.patch(
        `${BASE_URL}/api/admin/onboarding/existing/${userId}/document`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Document updated successfully");
      setExistingData((prev) =>
        prev.map((r) =>
          r.user_id === userId
            ? { ...r, id_document_s3_path: res.data.s3_key }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating existing document:", err);
      alert("Failed to update document. Please try again.");
    }
  };

  const deleteExistingSignature = async (userId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/onboarding/existing/${userId}/signature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Signature deleted successfully");
      setExistingData((prev) =>
        prev.map((r) =>
          r.user_id === userId ? { ...r, virtual_signature: null } : r
        )
      );
    } catch (err) {
      console.error("Error deleting existing signature:", err);
      alert("Failed to delete signature. Please try again.");
    }
  };

  const updateExistingSignature = async (userId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.patch(
        `${BASE_URL}/api/admin/onboarding/existing/${userId}/signature`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Signature updated successfully");
      setExistingData((prev) =>
        prev.map((r) =>
          r.user_id === userId
            ? { ...r, virtual_signature: res.data.s3_key }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating existing signature:", err);
      alert("Failed to update signature. Please try again.");
    }
  };

  // -------------------------------------------------------------------------
  // Delete / update helpers  –– NEW
  // -------------------------------------------------------------------------
  const deleteNewDocument = async (userId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/onboarding/new/${userId}/document`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Document deleted successfully");
      setNewData((prev) =>
        prev.map((r) =>
          r.user_id === userId ? { ...r, id_document_s3_path: null } : r
        )
      );
    } catch (err) {
      console.error("Error deleting new document:", err);
      alert("Failed to delete document. Please try again.");
    }
  };

  const updateNewDocument = async (userId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.patch(
        `${BASE_URL}/api/admin/onboarding/new/${userId}/document`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Document updated successfully");
      setNewData((prev) =>
        prev.map((r) =>
          r.user_id === userId
            ? { ...r, id_document_s3_path: res.data.s3_key }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating new document:", err);
      alert("Failed to update document. Please try again.");
    }
  };

  const deleteNewSignature = async (userId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/onboarding/new/${userId}/signature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Signature deleted successfully");
      setNewData((prev) =>
        prev.map((r) =>
          r.user_id === userId ? { ...r, virtual_signature: null } : r
        )
      );
    } catch (err) {
      console.error("Error deleting new signature:", err);
      alert("Failed to delete signature. Please try again.");
    }
  };

  const updateNewSignature = async (userId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.patch(
        `${BASE_URL}/api/admin/onboarding/new/${userId}/signature`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Signature updated successfully");
      setNewData((prev) =>
        prev.map((r) =>
          r.user_id === userId
            ? { ...r, virtual_signature: res.data.s3_key }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating new signature:", err);
      alert("Failed to update signature. Please try again.");
    }
  };

  // -------------------------------------------------------------------------
  // Delete / update helpers  –– CONSENT
  // -------------------------------------------------------------------------
  const deleteConsentDocument = async (consentId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}/document`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Consent document deleted successfully");
      setConsentData((prev) =>
        prev.map((r) =>
          r.consent_id === consentId ? { ...r, id_document_s3_path: null } : r
        )
      );
    } catch (err) {
      console.error("Error deleting consent document:", err);
      alert("Failed to delete consent document. Please try again.");
    }
  };

  const updateConsentDocument = async (consentId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.patch(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}/document`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Consent document updated successfully");
      setConsentData((prev) =>
        prev.map((r) =>
          r.consent_id === consentId
            ? { ...r, id_document_s3_path: res.data.s3_key }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating consent document:", err);
      alert("Failed to update consent document. Please try again.");
    }
  };

  const deleteConsentSignature = async (consentId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}/signature`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Consent signature deleted successfully");
      setConsentData((prev) =>
        prev.map((r) =>
          r.consent_id === consentId
            ? { ...r, electronic_signature: null }
            : r
        )
      );
    } catch (err) {
      console.error("Error deleting consent signature:", err);
      alert("Failed to delete signature. Please try again.");
    }
  };

  const updateConsentSignature = async (consentId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.patch(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}/signature`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Consent signature updated successfully");
      setConsentData((prev) =>
        prev.map((r) =>
          r.consent_id === consentId
            ? { ...r, electronic_signature: res.data.s3_key }
            : r
        )
      );
    } catch (err) {
      console.error("Error updating consent signature:", err);
      alert("Failed to update signature. Please try again.");
    }
  };

  // -------------------------------------------------------------------------
  // Delete entire records  (Existing / New / Request / Consent)
  // -------------------------------------------------------------------------
  const handleDeleteExistingRecord = async (userId) => {
    try {
      await axios.delete(`${BASE_URL}/api/admin/onboarding/existing/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("OnboardingExisting record deleted successfully.");
      setExistingData((prev) => prev.filter((r) => r.user_id !== userId));
    } catch (err) {
      console.error("Error deleting OnboardingExisting record:", err);
      alert(
        "Failed to delete entire record. Ensure the backend DELETE endpoint exists."
      );
    }
  };

  const handleDeleteNewRecord = async (userId) => {
    try {
      await axios.delete(`${BASE_URL}/api/admin/onboarding/new/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("OnboardingNew record deleted successfully.");
      setNewData((prev) => prev.filter((r) => r.user_id !== userId));
    } catch (err) {
      console.error("Error deleting OnboardingNew record:", err);
      alert(
        "Failed to delete entire record. Ensure the backend DELETE endpoint exists."
      );
    }
  };

  const handleDeleteRequestRecord = async (registrationId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/partner/account/registration/${registrationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("PartnerAccountRegistration record deleted successfully.");
      setRequestData((prev) =>
        prev.filter((r) => r.registration_id !== registrationId)
      );
    } catch (err) {
      console.error("Error deleting PartnerAccountRegistration record:", err);
      alert(
        "Failed to delete entire record. Ensure the backend DELETE endpoint exists."
      );
    }
  };

  const handleDeleteConsentRecord = async (consentId) => {
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/partner/account/consent/${consentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("PartnerAccountConsent record deleted successfully.");
      setConsentData((prev) =>
        prev.filter((r) => r.consent_id !== consentId)
      );
    } catch (err) {
      console.error("Error deleting PartnerAccountConsent record:", err);
      alert(
        "Failed to delete entire record. Ensure the backend DELETE endpoint exists."
      );
    }
  };

  // -------------------------------------------------------------------------
  // Approve onboarding‑new (creates CRM account)
  // -------------------------------------------------------------------------
  const handleApproveNewRecord = async (item) => {
    try {
      const auth = localStorage.getItem("adminToken");
      if (!auth) {
        alert("You must be logged in to approve.");
        return;
      }

      const codeInput = newUserCodes[item.user_id] || "";
      if (!codeInput) {
        alert("Please enter a User Code before approving.");
        return;
      }

      // 1) Build CRM payload
      const crmPayload = {
        unique_id: item.user_id,
        first_name: item.first_name,
        last_name: item.last_name,
        email_1: item.email1,
        email_2: item.email2 || "",
        date_of_birth: item.date_of_birth || "",
        address_line_1: item.address_line_1 || "",
        address_line_2: item.address_line_2 || "",
        address_city: item.address_city || "",
        address_state: "",
        address_zip: item.postcode || "",
        country: "",
        phone: item.phone_number || "",
        referral_1: item.referral1 || "",
        referral_2: item.referral2 || "",
        desired_events: [],
        presale_information: false,
        events_alert_service: false,
        express_transfer_service: Boolean(item.transfer_service),
        weekly_updates: false,
        weekly_portfolio: false,
        whatsapp_alerts: false,
        share_desired_events: false,
        permission_to_contact: false,
        tcs_company_policies: Boolean(item.service_agreement),
        portfolio_needed: false,
        browser: "",
        ip_address: "",
        location: {},
        user_code: codeInput,
        blocked: false,
        registered_at: "",
        filled_at: "",
        left_at: "",
      };

      // 2) Create user in CRM
      const { user_code: returnedCode, password } = await createPlatformUser(
        crmPayload,
        auth
      );
      alert(`CRM user created: ${returnedCode}`);

      // 3) Mark onboarding‑new as APPROVED
      await updateOnboardingNewStatus(item.user_id, "APPROVED", auth);

      // 4) Save CRM password back in DB
      if (password) {
        await patchOnboardingNewPassword(item.user_id, password, auth);
      }

      // 5) Update local state
      setNewData((prev) =>
        prev.map((r) =>
          r.user_id === item.user_id
            ? {
                ...r,
                status: "APPROVED",
                user_code: returnedCode,
                crm_password: password || "",
              }
            : r
        )
      );
      setNewUserCodes((codes) => ({ ...codes, [item.user_id]: returnedCode }));

      // 6) Compose full address
      const fullAddress = [
        item.address_line_1,
        item.address_line_2,
        item.address_city,
        item.county,
        item.postcode,
      ]
        .filter(Boolean)
        .join(", ");

      // 7) info.ttanalytics – create / update
      await axios.put(
        `${INFO_BASE_URL}/users/${returnedCode}`,
        {
          email: item.email1,
          full_name: `${item.first_name} ${item.last_name}`,
          onboarded: true,
          address: fullAddress,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      await axios.post(
        `${INFO_BASE_URL}/passwords/`,
        {
          user_code: returnedCode,
          site: "CRM Password",
          email: item.email1,
          password: password || "",
          main: true,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      await axios.put(
        `${INFO_BASE_URL}/users/${returnedCode}`,
        {
          full_name: `${item.first_name} ${item.last_name}`,
          address: fullAddress,
          DOB: item.date_of_birth || "",
          referral: item.referral1 || "",
          onboarded: true,
        },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
    } catch (err) {
      console.error("ApproveNewRecord error:", err);
      alert("Failed to approve and create user. Check console for details.");
    }
  };

  // -------------------------------------------------------------------------
  // Generic status change (Existing / New / Request / Consent)
  // -------------------------------------------------------------------------
  const handleStatusChange = async (formType, record, newStatus) => {
    try {
      const auth = localStorage.getItem("adminToken");
      if (!auth) return;

      if (formType === "existing") {
        await updateOnboardingExistingStatus(record.user_id, newStatus, auth);

        // sync to info.ttanalytics if needed
        if (record.user_code) {
          try {
            await axios.put(
              `${INFO_BASE_URL}/users/${record.user_code}/${
                newStatus.toUpperCase() === "APPROVED" ? "onboard" : "offboard"
              }`,
              null,
              { headers: { Authorization: `Bearer ${auth}` } }
            );
          } catch (err) {
            console.warn(
              "info.ttanalytics sync failed – non‑blocking:",
              err.message
            );
          }
        }

        setExistingData((prev) =>
          prev.map((r) =>
            r.user_id === record.user_id ? { ...r, status: newStatus } : r
          )
        );
      } else if (formType === "new") {
        await updateOnboardingNewStatus(record.user_id, newStatus, auth);
        setNewData((prev) =>
          prev.map((r) =>
            r.user_id === record.user_id ? { ...r, status: newStatus } : r
          )
        );
      } else if (formType === "request") {
        await updateSubAccountRequestStatus(record.registration_id, newStatus, auth);
        setRequestData((prev) =>
          prev.map((r) =>
            r.registration_id === record.registration_id
              ? { ...r, status: newStatus }
              : r
          )
        );
      } else if (formType === "consent") {
        await updateSubAccountConsentStatus(record.consent_id, newStatus, auth);
        setConsentData((prev) =>
          prev.map((r) =>
            r.consent_id === record.consent_id
              ? { ...r, status: newStatus }
              : r
          )
        );
      }
      alert(`Status updated to ${newStatus}!`);
    } catch (err) {
      console.error("Status update error:", err);
      alert("Failed to update status. Check logs or server response.");
    }
  };

  // -------------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------------
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    window.location.href = "/admin/login";
  };

  // -------------------------------------------------------------------------
  // Expand‑row toggles
  // -------------------------------------------------------------------------
  const toggleExistingExpand = (id) =>
    setExpandedExisting((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleNewExpand = (id) =>
    setExpandedNew((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleRequestExpand = (id) =>
    setExpandedRequest((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleConsentExpand = (id) =>
    setExpandedConsent((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // -------------------------------------------------------------------------
  // Render – Onboarding (Existing)  – EDIT button added
  // -------------------------------------------------------------------------
  const renderExistingTable = (records) => {
    if (!records?.length) return <p>No Onboarding (Existing) records found.</p>;

    return (
      <table
        style={{ marginTop: "1rem", width: "100%", borderCollapse: "collapse" }}
        border="1"
      >
        <thead>
          <tr>
            <th>user_id</th>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Document</th>
            <th>Signature</th>
            <th>Update/Delete Document</th>
            <th>Update/Delete Signature</th>
            <th>Delete Record</th>
            <th>Change Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.map((item) => {
            const isExpanded = expandedExisting.has(item.user_id);
            return (
              <React.Fragment key={item.user_id}>
                <tr>
                  <td>{item.user_id}</td>
                  <td>
                    {item.first_name} {item.last_name}
                  </td>
                  <td>{item.email1}</td>
                  <td>{item.status}</td>
                  <td>
                    <button onClick={() => viewExistingDocument(item.user_id)}>
                      View Doc
                    </button>
                  </td>
                  <td>
                    <button onClick={() => viewExistingSignature(item.user_id)}>
                      View Sig
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => deleteExistingDocument(item.user_id)}
                    >
                      Del Doc
                    </button>
                    <input
                      type="file"
                      onChange={(e) =>
                        e.target.files[0] &&
                        updateExistingDocument(item.user_id, e.target.files[0])
                      }
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => deleteExistingSignature(item.user_id)}
                    >
                      Del Sig
                    </button>
                    <input
                      type="file"
                      onChange={(e) =>
                        e.target.files[0] &&
                        updateExistingSignature(item.user_id, e.target.files[0])
                      }
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteExistingRecord(item.user_id)}
                    >
                      Delete Record
                    </button>
                  </td>
                  <td>
                    <select
                      value={item.status}
                      onChange={(e) =>
                        handleStatusChange("existing", item, e.target.value)
                      }
                    >
                      <option value="SUBMITTED">SUBMITTED</option>
                      <option value="REVIEWING">REVIEWING</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="REJECTED">REJECTED</option>
                      <option value="PENDING_INFO">PENDING_INFO</option>
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        downloadPdf(
                          `/admin/onboarding/existing/${item.user_id}/pdf`,
                          `onboarding_existing_${item.user_id}.pdf`
                        )
                      }
                      style={{ marginRight: 6 }}
                    >
                      Download PDF
                    </button>
                    <button
                      onClick={() =>
                        setEditing({ type: "existing", data: item })
                      }
                      style={{ marginRight: 6 }}
                    >
                      Edit
                    </button>
                    <button onClick={() => toggleExistingExpand(item.user_id)}>
                      {isExpanded ? "Hide Details" : "Details"}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={11}>
                      <div
                        style={{ padding: "1rem", background: "#f9f9f9" }}
                      >
                        <strong>All Fields (Existing):</strong>
                        <ul
                          style={{
                            listStyleType: "none",
                            paddingLeft: 0,
                            margin: 0,
                          }}
                        >
                          <li>primary_email: {item.email1 || ""}</li>
                          <li>date_of_birth: {item.date_of_birth || ""}</li>
                          <li>address_line_1: {item.address_line_1 || ""}</li>
                          <li>address_line_2: {item.address_line_2 || ""}</li>
                          <li>address_city: {item.address_city || ""}</li>
                          <li>postcode: {item.postcode || ""}</li>
                          <li>county: {item.county || ""}</li>
                          <li>phone_number: {item.phone_number || ""}</li>
                          <li>occupation: {item.occupation || ""}</li>
                          <li>working_hours: {item.working_hours || ""}</li>
                          <li>
                            payment_full_name: {item.payment_full_name || ""}
                          </li>
                          <li>
                            payment_sort_code: {item.payment_sort_code || ""}
                          </li>
                          <li>
                            payment_account_number:{" "}
                            {item.payment_account_number || ""}
                          </li>
                          <li>referral1: {item.referral1 || ""}</li>
                          
                          <li>user_code: {item.user_code || ""}</li>
                          <li>status_notes: {item.status_notes || ""}</li>
                          <li>
                            status_updated_at: {item.status_updated_at || ""}
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  // -------------------------------------------------------------------------
  // Render – Onboarding (New)   – EDIT button added
  // -------------------------------------------------------------------------
  const renderNewTable = (records) => {
    if (!records?.length) return <p>No Onboarding (New) records found.</p>;

    return (
      <table
        style={{ marginTop: "1rem", width: "100%", borderCollapse: "collapse" }}
        border="1"
      >
        <thead>
          <tr>
            <th>user_id</th>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>User Code</th>
            <th>Document</th>
            <th>Signature</th>
            <th>Update/Delete Document</th>
            <th>Update/Delete Signature</th>
            <th>Delete Record</th>
            <th>Change Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.map((item) => {
            const isExpanded = expandedNew.has(item.user_id);
            const codeValue = newUserCodes[item.user_id] || "";
            return (
              <React.Fragment key={item.user_id}>
                <tr>
                  <td>{item.user_id}</td>
                  <td>
                    {item.first_name} {item.last_name}
                  </td>
                  <td>{item.email1}</td>
                  <td>{item.status}</td>
                  <td>
                    <input
                      type="text"
                      value={codeValue}
                      onChange={(e) =>
                        setNewUserCodes((prev) => ({
                          ...prev,
                          [item.user_id]: e.target.value,
                        }))
                      }
                      placeholder="User Code"
                      style={{ width: "100px" }}
                    />
                  </td>
                  <td>
                    <button onClick={() => viewNewDocument(item.user_id)}>
                      View Doc
                    </button>
                  </td>
                  <td>
                    <button onClick={() => viewNewSignature(item.user_id)}>
                      View Sig
                    </button>
                  </td>
                  <td>
                    <button onClick={() => deleteNewDocument(item.user_id)}>
                      Del Doc
                    </button>
                    <input
                      type="file"
                      onChange={(e) =>
                        e.target.files[0] &&
                        updateNewDocument(item.user_id, e.target.files[0])
                      }
                    />
                  </td>
                  <td>
                    <button onClick={() => deleteNewSignature(item.user_id)}>
                      Del Sig
                    </button>
                    <input
                      type="file"
                      onChange={(e) =>
                        e.target.files[0] &&
                        updateNewSignature(item.user_id, e.target.files[0])
                      }
                    />
                  </td>
                  <td>
                    <button onClick={() => handleDeleteNewRecord(item.user_id)}>
                      Delete Record
                    </button>
                  </td>
                  <td>
                    <select
                      value={item.status}
                      onChange={(e) =>
                        handleStatusChange("new", item, e.target.value)
                      }
                    >
                      <option value="SUBMITTED">SUBMITTED</option>
                      <option value="REVIEWING">REVIEWING</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="REJECTED">REJECTED</option>
                      <option value="PENDING_INFO">PENDING_INFO</option>
                    </select>
                  </td>
                  <td>
                    <button onClick={() => handleApproveNewRecord(item)}>
                      Approve
                    </button>
                    <button
                      onClick={() =>
                        downloadPdf(
                          `/admin/onboarding/new/${item.user_id}/pdf`,
                          `onboarding_new_${item.user_id}.pdf`
                        )
                      }
                      style={{ marginLeft: 6, marginRight: 6 }}
                    >
                      Download PDF
                    </button>
                    <button
                      onClick={() => setEditing({ type: "new", data: item })}
                      style={{ marginRight: 6 }}
                    >
                      Edit
                    </button>
                    <button onClick={() => toggleNewExpand(item.user_id)}>
                      {isExpanded ? "Hide Details" : "Details"}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={12}>
                      <div
                        style={{ padding: "1rem", background: "#f9f9f9" }}
                      >
                        <strong>All Fields (New):</strong>
                        <ul
                          style={{
                            listStyleType: "none",
                            paddingLeft: 0,
                            margin: 0,
                          }}
                        >
                          <li>primary_email: {item.email1 || ""}</li>
                          <li>email2: {item.email2 || ""}</li>
                          <li>date_of_birth: {item.date_of_birth || ""}</li>
                          <li>address_line_1: {item.address_line_1 || ""}</li>
                          <li>address_line_2: {item.address_line_2 || ""}</li>
                          <li>address_city: {item.address_city || ""}</li>
                          <li>postcode: {item.postcode || ""}</li>
                          <li>county: {item.county || ""}</li>
                          <li>phone_number: {item.phone_number || ""}</li>
                          <li>occupation: {item.occupation || ""}</li>
                          <li>working_hours: {item.working_hours || ""}</li>
                          <li>
                            payment_full_name: {item.payment_full_name || ""}
                          </li>
                          <li>
                            payment_sort_code: {item.payment_sort_code || ""}
                          </li>
                          <li>
                            payment_account_number:{" "}
                            {item.payment_account_number || ""}
                          </li>
                          <li>referral1: {item.referral1 || ""}</li>
                          <li>referral2: {item.referral2 || ""}</li>
                          <li>user_code: {item.user_code || ""}</li>
                          <li>crm_password: {item.crm_password || ""}</li>
                          <li>status_notes: {item.status_notes || ""}</li>
                          <li>
                            status_updated_at: {item.status_updated_at || ""}
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  // -------------------------------------------------------------------------
  // Render – Sub‑Account Request (unchanged)
  // -------------------------------------------------------------------------
  const renderRequestTable = (records) => {
    if (!records?.length) return <p>No Sub‑Account Request records found.</p>;

    return (
      <table
        style={{ marginTop: "1rem", width: "100%", borderCollapse: "collapse" }}
        border="1"
      >
        <thead>
          <tr>
            <th>registration_id</th>
            <th>Main User</th>
            <th>Partner</th>
            <th>Status</th>
            <th>Consent Link</th>
            <th>Delete Record</th>
            <th>Change Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.map((item) => {
            const isExpanded = expandedRequest.has(item.registration_id);
            return (
              <React.Fragment key={item.registration_id}>
                <tr>
                  <td>{item.registration_id}</td>
                  <td>
                    {item.main_first_name} {item.main_last_name}
                  </td>
                  <td>
                    {item.partner_first_name} {item.partner_last_name}
                  </td>
                  <td>{item.status}</td>
                  <td>
                    {item.consent_link ? (
                      <a
                        href={item.consent_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Link
                      </a>
                    ) : (
                      "No Link"
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        handleDeleteRequestRecord(item.registration_id)
                      }
                    >
                      Delete Record
                    </button>
                  </td>
                  <td>
                    <select
                      value={item.status}
                      onChange={(e) =>
                        handleStatusChange("request", item, e.target.value)
                      }
                    >
                      <option value="SUBMITTED">SUBMITTED</option>
                      <option value="REVIEWING">REVIEWING</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="REJECTED">REJECTED</option>
                      <option value="PENDING_INFO">PENDING_INFO</option>
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        toggleRequestExpand(item.registration_id)
                      }
                    >
                      {isExpanded ? "Hide Details" : "View Details"}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={8}>
                      <div
                        style={{ padding: "1rem", background: "#f9f9f9" }}
                      >
                        <strong>All Fields (Request):</strong>
                        <ul
                          style={{
                            listStyleType: "none",
                            paddingLeft: 0,
                            margin: 0,
                          }}
                        >
                          <li>
                            main_first_name: {item.main_first_name || ""}
                          </li>
                          <li>main_last_name: {item.main_last_name || ""}</li>
                          <li>main_user_code: {item.main_user_code || ""}</li>
                          <li>main_email: {item.main_email || ""}</li>
                          <li>
                            partner_first_name: {item.partner_first_name || ""}
                          </li>
                          <li>
                            partner_last_name: {item.partner_last_name || ""}
                          </li>
                          <li>
                            partner_email: {item.partner_email || ""}
                          </li>
                          <li>
                            partner_phone_number:{" "}
                            {item.partner_phone_number || ""}
                          </li>
                          <li>
                            address_line_1: {item.address_line_1 || ""}
                          </li>
                          <li>
                            address_line_2: {item.address_line_2 || ""}
                          </li>
                          <li>address_city: {item.address_city || ""}</li>
                          <li>postcode: {item.postcode || ""}</li>
                          <li>county: {item.county || ""}</li>
                          <li>
                            consent_requirement:{" "}
                            {item.consent_requirement ? "True" : "False"}
                          </li>
                          <li>
                            allow_email_creation:{" "}
                            {item.allow_email_creation ? "True" : "False"}
                          </li>
                          <li>
                            compliance_agreement:{" "}
                            {item.compliance_agreement ? "True" : "False"}
                          </li>
                          <li>
                            partner_account_consent:{" "}
                            {item.partner_account_consent ? "True" : "False"}
                          </li>
                          <li>
                            submission_date: {item.submission_date || ""}
                          </li>
                          <li>status_notes: {item.status_notes || ""}</li>
                          <li>
                            status_updated_at: {item.status_updated_at || ""}
                          </li>
                          <li>consent_link: {item.consent_link || "No Link"}</li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  // -------------------------------------------------------------------------
  // Render – Sub‑Account Consent (unchanged)
  // -------------------------------------------------------------------------
  const renderConsentTable = (records) => {
    if (!records?.length) return <p>No Sub‑Account Consent records found.</p>;

    return (
      <table
        style={{ marginTop: "1rem", width: "100%", borderCollapse: "collapse" }}
        border="1"
      >
        <thead>
          <tr>
            <th>consent_id</th>
            <th>Partner Name</th>
            <th>Main Name</th>
            <th>Status</th>
            <th>Submission Date</th>
            <th>Status Updated</th>
            <th>View Document</th>
            <th>View Signature</th>
            <th>Update/Delete Document</th>
            <th>Update/Delete Signature</th>
            <th>Delete Record</th>
            <th>Change Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.map((item) => {
            const isExpanded = expandedConsent.has(item.consent_id);
            return (
              <React.Fragment key={item.consent_id}>
                <tr>
                  <td>{item.consent_id}</td>
                  <td>
                    {item.partner_first_name} {item.partner_last_name}
                  </td>
                  <td>
                    {item.main_first_name} {item.main_last_name}
                  </td>
                  <td>{item.status}</td>
                  <td>{item.submission_date || ""}</td>
                  <td>{item.status_updated_at || ""}</td>
                  <td>
                    <button onClick={() => viewConsentDocument(item.consent_id)}>
                      Doc
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => viewConsentSignature(item.consent_id)}
                    >
                      Sig
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => deleteConsentDocument(item.consent_id)}
                    >
                      Del Doc
                    </button>
                    <input
                      type="file"
                      onChange={(e) =>
                        e.target.files[0] &&
                        updateConsentDocument(
                          item.consent_id,
                          e.target.files[0]
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => deleteConsentSignature(item.consent_id)}
                    >
                      Del Sig
                    </button>
                    <input
                      type="file"
                      onChange={(e) =>
                        e.target.files[0] &&
                        updateConsentSignature(
                          item.consent_id,
                          e.target.files[0]
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteConsentRecord(item.consent_id)}
                    >
                      Delete Record
                    </button>
                  </td>
                  <td>
                    <select
                      value={item.status}
                      onChange={(e) =>
                        handleStatusChange("consent", item, e.target.value)
                      }
                    >
                      <option value="SUBMITTED">SUBMITTED</option>
                      <option value="REVIEWING">REVIEWING</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="REJECTED">REJECTED</option>
                      <option value="PENDING_INFO">PENDING_INFO</option>
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        downloadPdf(
                          `/admin/partner/account/consent/${item.consent_id}/pdf`,
                          `partner_account_consent_${item.consent_id}.pdf`
                        )
                      }
                      style={{ marginRight: 6 }}
                    >
                      Download PDF
                    </button>
                    <button
                      onClick={() => toggleConsentExpand(item.consent_id)}
                    >
                      {isExpanded ? "Hide Details" : "View Details"}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={13}>
                      <div
                        style={{ padding: "1rem", background: "#f9f9f9" }}
                      >
                        <strong>All Fields (Consent):</strong>
                        <ul
                          style={{
                            listStyleType: "none",
                            paddingLeft: 0,
                            margin: 0,
                          }}
                        >
                          <li>
                            partner_first_name: {item.partner_first_name}
                          </li>
                          <li>
                            partner_last_name: {item.partner_last_name}
                          </li>
                          <li>main_first_name: {item.main_first_name}</li>
                          <li>main_last_name: {item.main_last_name}</li>
                          <li>main_user_code: {item.main_user_code || ""}</li>
                          <li>main_email: {item.main_email || ""}</li>
                          <li>partner_email: {item.partner_email || ""}</li>
                          <li>
                            submission_date: {item.submission_date || ""}
                          </li>
                          <li>
                            consent_for_purchases:{" "}
                            {item.consent_for_purchases ? "True" : "False"}
                          </li>
                          <li>
                            agree_terms: {item.agree_terms ? "True" : "False"}
                          </li>
                          <li>
                            compliance_agreement:{" "}
                            {item.compliance_agreement ? "True" : "False"}
                          </li>
                          <li>
                            confirm_correct_information:{" "}
                            {item.confirm_correct_information
                              ? "True"
                              : "False"}
                          </li>
                          <li>
                            data_sharing_privacy_consent:{" "}
                            {item.data_sharing_privacy_consent
                              ? "True"
                              : "False"}
                          </li>
                          <li>
                            liability_and_responsibility:{" "}
                            {item.liability_and_responsibility
                              ? "True"
                              : "False"}
                          </li>
                          <li>phone_number: {item.phone_number || ""}</li>
                          <li>address_line_1: {item.address_line_1 || ""}</li>
                          <li>address_line_2: {item.address_line_2 || ""}</li>
                          <li>address_city: {item.address_city || ""}</li>
                          <li>postcode: {item.postcode || ""}</li>
                          <li>county: {item.county || ""}</li>
                          <li>
                            id_document_s3_path:{" "}
                            {item.id_document_s3_path || ""}
                          </li>
                          <li>
                            electronic_signature:{" "}
                            {item.electronic_signature || ""}
                          </li>
                          <li>status_notes: {item.status_notes || ""}</li>
                          <li>
                            status_updated_at: {item.status_updated_at || ""}
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------
  return (
    <div style={{ padding: "1rem" }}>
      <h1>Admin Dashboard</h1>

      <div style={{ marginBottom: "1rem" }}>
        <button onClick={() => setActiveTab("existing")}>
          Onboarding (Existing)
        </button>
        <button onClick={() => setActiveTab("new")}>Onboarding (New)</button>
        <button onClick={() => setActiveTab("request")}>
          Sub‑Account Request
        </button>
        <button onClick={() => setActiveTab("consent")}>
          Sub‑Account Consent
        </button>
        <button onClick={handleLogout} style={{ marginLeft: "1rem" }}>
          Logout
        </button>
      </div>

      {error && (
        <div style={{ color: "red", marginBottom: "1rem" }}>{error}</div>
      )}

      {activeTab === "existing" && (
        <div>
          <h2>Onboarding (Existing)</h2>
          <button
            onClick={() =>
              exportExcel(
                "/admin/export/onboarding/existing",
                "OnboardingExisting"
              )
            }
          >
            Export to Excel
          </button>
          {renderExistingTable(existingData)}
        </div>
      )}

      {activeTab === "new" && (
        <div>
          <h2>Onboarding (New)</h2>
          <button
            onClick={() =>
              exportExcel("/admin/export/onboarding/new", "OnboardingNew")
            }
          >
            Export to Excel
          </button>
          {renderNewTable(newData)}
        </div>
      )}

      {activeTab === "request" && (
        <div>
          <h2>Sub‑Account Requests</h2>
          <button
            onClick={() =>
              exportExcel(
                "/admin/export/partner/account/registration",
                "PartnerAccountRegistration"
              )
            }
          >
            Export to Excel
          </button>
          {renderRequestTable(requestData)}
        </div>
      )}

      {activeTab === "consent" && (
        <div>
          <h2>Sub‑Account Consents</h2>
          <button
            onClick={() =>
              exportExcel(
                "/admin/export/partner/account/consent",
                "PartnerAccountConsent"
              )
            }
          >
            Export to Excel
          </button>
          {renderConsentTable(consentData)}
        </div>
      )}

      {/* 🆕 Edit modal */}
      {editing && (
        <EditDialog
          open
          type={editing.type}
          record={editing.data}
          onClose={() => setEditing(null)}
          onSave={(patch) => handleEditSave(patch, editing.data)}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
